<template lang="html">
  <default-layout>
    <div v-if="!my_submission" key="loading" class="text-center mt-10">
      <p class="mb-2.5">Loading Submission</p>
      <div class="flex justify-center">
        <pl-spinner />
      </div>
    </div>
    <div v-else>
      <div class="max-w-screen-2xl md:flex justify-between items-center pb-5">
        <div class="flex flex-row items-center">
          <div class="text-2xl font-bold">
            {{ getCurrentRT.title }}
          </div>
        </div>
        <identity-reveal
          v-if="getCurrentRT.enable_annonymous && !hasTeacherPermissionsInCourse"
          class="mt-1 md:mt-0"
        />
      </div>
      <div v-if="windowWidth < 1024">
        <button
          class="mb-5 px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold"
          @click="openPromptInModal"
        >
          Prompt
        </button>
      </div>
      <div class="max-w-screen-2xl submission-response-body flex gap-5">
        <div
          class="bg-white text-sm rounded border border-coolGray-200 p-5"
          :class="toggle_prompt ? ['w-1/2'] : ['w-full']"
        >
          <div class="w-full flex justify-between pb-2.5 border-b">
            <div class="text-base font-semibold text-gray-500">Submission</div>
            <div v-if="windowWidth >= 1024">
              <span class="text-gray-500 text-xs mr-2">Discussion Prompt</span>
              <toggle-button
                :sync="true"
                :labels="false"
                v-model="toggle_prompt"
                color="#72ccd7"
                class="toggle-button-class"
              />
            </div>
          </div>
          <submission-form
            class="pt-5"
            v-if="my_submission"
            :submissionData="my_submission"
          />
        </div>
        <div
          v-if="toggle_prompt"
          class="w-1/2 bg-white text-sm rounded border border-coolGray-200 p-5"
        >
          <div class="w-full flex justify-between pb-2.5 border-b">
            <div class="text-base font-semibold text-gray-500">
              Discussion Prompt
            </div>
            <div class="text-sm text-gray-500">by {{ author_name }}</div>
          </div>
          <prompt class="pt-2.5 text-sm leading-7" />
        </div>
      </div>
      <div v-if="prompt_in_modal">
        <pl-dialog
          :size="'md'"
          :title="'Prompt'"
          @close="prompt_in_modal = false"
        >
          <div class="px-7 overflow-auto">
            <prompt class="pt-4 text-sm leading-7" />
          </div>
        </pl-dialog>
      </div>
    </div>
  </default-layout>
</template>

<script>
  import defaultLayout from 'layouts/default/default.vue';
  import prompt from 'features/onlineRoundtables/components/promptInSubmitPage.vue';
  import submissionForm from 'features/onlineRoundtables/components/submissionForm/submissionForm.vue';
  import { mapGetters } from 'vuex';
  import find from 'lodash/find';
  import roundtableService from 'features/onlineRoundtables/onlineRoundtables.services';
  import identityReveal from 'components/identityReveal/identityReveal';
  export default {
    data() {
      return {
        my_submission: null,
        toggle_prompt: true,
        windowWidth: window.innerWidth,
        prompt_in_modal: false,
      };
    },
    components: {
      defaultLayout,
      prompt,
      submissionForm,
      identityReveal,
    },
    computed: {
      ...mapGetters([
        'getMyRTUserDetails',
        'getCurrentRT',
        'getProfile',
        'getCourseDetail',
        'hasTeacherPermissionsInCourse',
        'getRoundtableAuthor',
      ]),
      submission() {
        const current_submission = this.getCurrentRT.submissions.find((s) => s.id === this.getMyRTUserDetails.submission_id);
        return current_submission || this.my_submission;
      },
      connected_to_google() {
        return (
          Object.keys(this.getProfile.google_integration).length &&
          this.getProfile.google_integration.connected
        );
      },
      connected_to_rt_integration() {
        if (
          this.getProfile.edlink_integrations.length &&
          this.getCurrentRT.edlink_integrations.length
        ) {
          const integration = this.getProfile.edlink_integrations.find(
            (i) => i.id === this.getCurrentRT.edlink_integrations[0].id
          );
          return !!integration;
        }
        return false;
      },
      submission_google_integration() {
        return (
          this.getCurrentRT.google_classroom_course_work_id !== null &&
          this.submission.google_classroom_integration.connected
        );
      },
      submission_rt_integration() {
        const { edlink_integrations: submission_integrations } =
          this.submission;
        const { edlink_integrations: rt_integrations } = this.getCurrentRT;
        if (
          rt_integrations &&
          rt_integrations.length &&
          submission_integrations.length
        ) {
          const rt_integration = rt_integrations[0]; // just one per roundtable
          return submission_integrations.find(
            (sub) => sub.id === rt_integration.id
          );
        }
        return false;
      },
      author_name() {
        return (
          this.getRoundtableAuthor.preferred_name ||
          this.getRoundtableAuthor.name
        );
      },
    },

    async created() {
      if (!this.getCourseDetail.id) {
        await this.$store.dispatch('enterCourse', this.$route.params.courseID);
      }
      this.$store
        .dispatch('fetchRoundtable', this.$route.params.roundtableID)
        .then(async () => {
          await this.$store.dispatch(
            'myRTIdentity',
            this.$route.params.roundtableID
          );

          this.$store.dispatch('openOnlineRoundtable', { id: this.getMyRTUserDetails.submission_id, rtId: this.$route.params.roundtableID });

          return roundtableService
            .viewSubmission(this.getMyRTUserDetails.submission_id)
            .then((res) => {
              this.my_submission = find(this.getCurrentRT.submissions, {
                id: this.getMyRTUserDetails.submission_id,
              });
              this.my_submission.prompt_response = res.data.prompt_response;
            });
        });
      window.addEventListener('resize', this.onResize);
    },
    mounted() {
      this.$store.commit('CHANGE_FULL_SCREEN_ON_COURSE_LAYOUT', true);
      this.$store.commit('SHOW_SLIDING_HIDDEN_ROUNDTABLE_LIST', false);
      if (
        (this.submission_google_integration && !this.connected_to_google) ||
        (this.submission_rt_integration && !this.connected_to_rt_integration)
      ) {
        sessionStorage.setItem('course_id', this.getCourseDetail.id);
        sessionStorage.setItem('activity_id', this.getCurrentRT.id);
        sessionStorage.setItem('activity_type', 'online');
        let route = {};
        route = {
          name: 'student.settings',
          params: {
            courseID: this.$route.params.courseID,
          },
          query: {
            roundtableID: this.$route.params.roundtableID,
          },
        };

        this.$router.push(route);
      }

      this.$nextTick(() => {
        window.addEventListener('resize', this.onResize);
        this.windowWidth = window.innerWidth;
        if (this.windowWidth < 1024) {
          this.toggle_prompt = false;
        }
      });
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onResize);
    },
    methods: {
      onResize() {
        this.windowWidth = window.innerWidth;
        if (this.windowWidth < 1024) {
          this.toggle_prompt = false;
        }
      },
      openPromptInModal() {
        this.prompt_in_modal = true;
      },
    },
  };
</script>

<style lang="stylus" scoped>
  .submission-response-body
    min-height: calc(100vh - 144px);
</style>


// WEBPACK FOOTER //
// submitResponsePage.vue?2fe410e2